import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header, SEOHead } from '../components';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  ComputerDesktopIcon,
  CodeBracketIcon,
  PaintBrushIcon,
  CogIcon,
  StarIcon,
  UserGroupIcon,
  ChartBarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  CheckIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

const WebsiteDevelopmentPage: React.FC = () => {
  const navigate = useNavigate();

  // SEO setup
  useSEO(pageConfigs.websiteDevelopment);

  const handleCallPhone = () => {
    window.location.href = 'tel:+919711834001';
  };

  const handleSendEmail = () => {
    window.location.href = 'mailto:info@3arrowsmedia.com';
  };

  const features = [
    {
      title: "Custom Website Design",
      description: "Create stunning, unique websites that perfectly represent your brand and engage your visitors.",
      icon: <PaintBrushIcon className="w-8 h-8" />,
      benefits: ["Brand-focused design", "User experience optimization", "Responsive layouts", "Modern aesthetics"]
    },
    {
      title: "E-commerce Development",
      description: "Build powerful online stores that drive sales and provide seamless shopping experiences.",
      icon: <ShoppingBagIcon className="w-8 h-8" />,
      benefits: ["Payment integration", "Inventory management", "Order processing", "Customer accounts"]
    },
    {
      title: "SEO Optimization",
      description: "Optimize your website for search engines to increase visibility and drive organic traffic.",
      icon: <MagnifyingGlassIcon className="w-8 h-8" />,
      benefits: ["Technical SEO", "Content optimization", "Local SEO", "Performance optimization"]
    },
    {
      title: "CMS Integration",
      description: "Easy-to-manage content management systems that give you full control over your website.",
      icon: <CogIcon className="w-8 h-8" />,
      benefits: ["WordPress", "Shopify", "Custom CMS", "Content editing tools"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Strategy",
      description: "We analyze your business goals, target audience, and requirements to create a comprehensive website strategy."
    },
    {
      step: "02",
      title: "Design & Wireframing",
      description: "Create wireframes, mockups, and design concepts that align with your brand and business objectives."
    },
    {
      step: "03",
      title: "Development & Testing",
      description: "Build your website using modern technologies with thorough testing and quality assurance."
    },
    {
      step: "04",
      title: "Launch & Optimization",
      description: "Deploy your website and continuously optimize for performance, SEO, and user experience."
    }
  ];

  const caseStudy = {
    client: "E-commerce Retailer",
    industry: "Retail",
    challenge: "Outdated website with poor user experience and low conversion rates",
    solution: "Developed a modern, mobile-first e-commerce website with advanced features",
    results: [
      "300% increase in conversion rate",
      "50% improvement in page load speed",
      "200% increase in mobile sales",
      "ROI of 400% in first year"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.websiteDevelopment} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
                <ComputerDesktopIcon className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 font-semibold text-sm">Web Development Experts</span>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
                Website 
                <span className="text-blue-600"> Development</span>
              </h1>
              
              <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                Create stunning, high-performing websites that convert visitors into customers. 
                Our custom web development solutions drive results and grow your business.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link 
                  to="/quote"
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Start Your Project</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link 
                  to="/portfolio"
                  className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors text-center"
                >
                  View Portfolio
                </Link>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">300%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Conversion Increase</div>
                  <div className="text-xs text-slate-500">Average improvement</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">50%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Faster Loading</div>
                  <div className="text-xs text-slate-500">Page speed improvement</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">200+</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Websites Built</div>
                  <div className="text-xs text-slate-500">Successfully delivered</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">400%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">ROI</div>
                  <div className="text-xs text-slate-500">Average return</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <ComputerDesktopIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free Website Audit</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Get a comprehensive analysis of your current website and receive 
                  actionable recommendations to improve performance and conversions.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Performance analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">SEO optimization review</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Conversion optimization tips</span>
                  </div>
                </div>
                
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                  Get Your Free Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Comprehensive Web Development
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From simple landing pages to complex e-commerce platforms, we build websites that drive results
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{feature.icon}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckIcon className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Our Development Process
            </h2>
            <p className="text-xl text-slate-600">
              Proven methodology for successful website development
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-100 rounded-full px-4 py-2 mb-6">
                  <StarIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-semibold text-sm">Success Story</span>
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                  {caseStudy.client}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                    <p className="text-slate-600">{caseStudy.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Solution</h4>
                    <p className="text-slate-600">{caseStudy.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4">Results</h4>
                    <div className="space-y-3">
                      {caseStudy.results.map((result, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <CheckIcon className="w-5 h-5 text-green-500" />
                          <span className="text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-display font-bold text-slate-900 mb-6">Ready to Build Your Website?</h3>
                <p className="text-slate-600 mb-6">
                  Join hundreds of businesses that have transformed their online presence with our expert web development.
                </p>
                
                <div className="space-y-4">
                  <Link 
                    to="/quote"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center block"
                  >
                    Start Your Project
                  </Link>
                  <Link 
                    to="/portfolio"
                    className="w-full bg-white hover:bg-gray-50 text-slate-900 font-semibold py-3 px-6 rounded-lg border-2 border-slate-200 transition-colors text-center block"
                  >
                    View Portfolio
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-6">
            Ready to Build Your Website?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Let's discuss your website project and create a development plan that drives results for your business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleCallPhone}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <PhoneIcon className="w-5 h-5" />
              <span>Call (+91) 9711834001</span>
            </button>
            <button 
              onClick={handleSendEmail}
              className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <EnvelopeIcon className="w-5 h-5" />
              <span>Send Email</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default WebsiteDevelopmentPage;
